import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import CustomDropdown from './CustomDropdown';
import DualChoiceButtons from './DualChoiceButtons';
import { highlightList, specialZoneMartList } from '../../helpers/lists';
import { SalePhaseData } from '../../model/model';
import TooltipLabel from './TooltipLabel';
import { multivarianceInfo } from '../../helpers/tooltips';
import { demoState } from '../../states/DemoState';
import { useRecoilValue } from 'recoil';
import { PlayArrow } from '@mui/icons-material';
import CustomInput from './CustomInput';

const PhaseContainer = styled.div`
    display: flex;
    flex: 15;
    flex-direction: column;
    border: 2px solid #58585A;
    overflow: hidden;
    border-radius: 30px;
    margin-bottom: 1rem;
`;

const PhaseExpandButton = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    cursor: pointer;
`;

const Header = styled.div<{ $isExpanded: boolean }>`
    width: 100%;
    display: flex;
    border-bottom: ${({ $isExpanded }) => ($isExpanded ? '2px solid #58585A' : 'none')};
`;

const PhaseNumber = styled.span`
    flex: 1.5;
    text-align: center;
`;

const RotatingPlayArrow = styled(PlayArrow) <{ $isExpanded: boolean }>`
    transition: transform 0.3s ease !important;
    margin-top: 1.5rem;
    margin-right: 0.5rem;
    transform: translateY(-50%);
    ${({ $isExpanded }) => $isExpanded ? 'transform: translateY(-50%) rotate(90deg);' : 'transform: translateY(-50%) rotate(0deg);'}
`;

const disabledStyle = css`
    background-color: #fbd1d9 !important;
    pointer-events: none !important;
`;

const HeaderInput = styled.input<{ isDisabled?: boolean }>`
    flex: 1 !important;
    width: 100% !important;
    padding: 0 !important;
    text-align: center !important;
    border: none !important;
    border-left: 2px solid #58585A !important; 
    ${({ isDisabled }) => isDisabled && disabledStyle}
`;

const Content = styled.div<{ $isExpanded: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: ${({ $isExpanded }) => ($isExpanded ? '1000px' : '0')};
    opacity: ${({ $isExpanded }) => ($isExpanded ? '1' : '0')};
    margin: ${({ $isExpanded }) => ($isExpanded ? '1rem' : '0')};
    transition: max-height 0.5s ease-in-out, opacity 0.3s ease-in-out;
`;

const ContentItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
`;

const Label = styled.label`
    flex: 1;
    font-size: 0.9rem;
    text-align: center;
`;

interface Props {
    salePhase: SalePhaseData;
    onPhaseChange: (phase: SalePhaseData) => void;
}

const Phase: React.FC<Props> = ({ salePhase, onPhaseChange }) => {
    const uniqueId = salePhase.uniqueId;
    const phaseNumber = salePhase.phaseNumber;
    const [isExpanded, setIsExpanded] = useState(false);
    const [durationInDays, setDurationInDays] = useState(salePhase.durationInDays);
    const [soldItemsCount, setSoldItemsCount] = useState(salePhase.soldItemsCount);
    const [salePriceGross, setSalePriceGross] = useState(salePhase.salePriceGross);
    const [specialZoneMart, setSpecialZoneMart] = useState(0); // Temporarly replaced salePhase.specialZoneMart with '0' to always return "brak" value for specialZoneMart 
    const [isSpecialZoneMulti, setIsSpecialZoneMulti] = useState(salePhase.isSpecialZoneMulti);
    const [highlight, setHighlight] = useState(salePhase.highlight);
    const [isPromoted, setIsPromoted] = useState(salePhase.isPromoted);
    const [includesCoinsCost, setIncludesCoinsCost] = useState(salePhase.includesCoinsCost);
    const [allegroAdsBudgetGross, setAllegroAdsBudgetGross] = useState(salePhase.allegroAdsBudgetGross);
    const demo = useRecoilValue(demoState);

    useEffect(() => {
        onPhaseChange({
            uniqueId,
            phaseNumber,
            durationInDays,
            soldItemsCount,
            salePriceGross,
            specialZoneMart,
            isSpecialZoneMulti,
            highlight,
            isPromoted,
            includesCoinsCost,
            allegroAdsBudgetGross
        });
    }, [phaseNumber, durationInDays, soldItemsCount, salePriceGross, specialZoneMart,
        isSpecialZoneMulti, highlight, isPromoted, includesCoinsCost, allegroAdsBudgetGross]);

    return (
        <PhaseContainer>
            <Header $isExpanded={isExpanded}>
                <PhaseExpandButton onClick={() => setIsExpanded(!isExpanded)}>
                    <PhaseNumber>{phaseNumber}</PhaseNumber>
                    <RotatingPlayArrow fontSize='medium' $isExpanded={isExpanded} />
                </PhaseExpandButton>
                <HeaderInput
                    type="number"
                    value={!isNaN(durationInDays) ? durationInDays : 0}
                    onChange={e => setDurationInDays(parseInt(e.target.value))}
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => e.currentTarget.select()}
                    isDisabled={demo}
                />
                <HeaderInput
                    type="number"
                    value={!isNaN(soldItemsCount) ? soldItemsCount : 0}
                    onChange={e => setSoldItemsCount(parseInt(e.target.value))}
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => e.currentTarget.select()}
                    isDisabled={demo}
                />
                <CustomInput
                    value={salePriceGross}
                    onChange={setSalePriceGross}
                    type='currency'
                    phase={true}
                />
            </Header>
            <Content $isExpanded={isExpanded}>
                {isExpanded && <>
                    {/* <ContentItem>
                        <Label>Strefa okazji: Oznaczenie</Label> // Temporarly removed specialZoneMart field, to always return "brak" value for specialZoneMart
                        <CustomDropdown
                            options={specialZoneMartList}
                            defaultValue={specialZoneMart}
                            onChange={value => (setSpecialZoneMart(value))}
                            variant="phase"
                        />
                    </ContentItem> */}
                    <ContentItem>
                        <TooltipLabel label="Strefa okazji: Wielowariantowość" tooltip={multivarianceInfo} />
                        <DualChoiceButtons
                            option1="Tak"
                            option2="Nie"
                            defaultOption={isSpecialZoneMulti ? 'Tak' : 'Nie'}
                            onSelectionChange={option => setIsSpecialZoneMulti(option === 'Tak')}
                        />
                    </ContentItem>
                    <ContentItem>

                        <Label>Rodzaj wyróżnienia</Label>
                        <CustomDropdown
                            options={highlightList}
                            defaultValue={highlight}
                            onChange={value => (setHighlight(value))}
                            variant="phase"
                        />
                    </ContentItem>
                    <ContentItem>

                        <Label>Promowanie na stronie działu</Label>
                        <DualChoiceButtons
                            option1="Tak"
                            option2="Nie"
                            defaultOption={isPromoted ? 'Tak' : 'Nie'}
                            onSelectionChange={option => setIsPromoted(option === 'Tak')}
                        />
                    </ContentItem>
                    <ContentItem>

                        <Label>Monety Allegro</Label>
                        <DualChoiceButtons
                            option1="Tak"
                            option2="Nie"
                            defaultOption={includesCoinsCost ? 'Tak' : 'Nie'}
                            onSelectionChange={option => setIncludesCoinsCost(option === 'Tak')}
                        />
                    </ContentItem>
                    <ContentItem>
                        <Label>Budżet AllegroAds</Label>
                        <CustomInput
                            value={allegroAdsBudgetGross}
                            onChange={setAllegroAdsBudgetGross}
                            type='currency'
                        />
                    </ContentItem>
                </>}
            </Content>
        </PhaseContainer >
    );
}

export default Phase;
